import { StructuredDataAuthorProps, StructuredDataType } from '@/shared/types/structured-data';
import { isStaging } from '@/shared/utils/utils';
import Head from 'next/head';
import Script from 'next/script';
import React from 'react';
import StructuredDataNewsArticle from '../../StructuredData/NewsArticle';

interface IProps {
  title?: string;
  description?: string;
  image?: string;
  url?: string;
  scripts?: string[];
  noIndex?: boolean;
  structuredDataType?: StructuredDataType;
  author?: StructuredDataAuthorProps[];
  datePublished?: string;
  dateModified?: string;
  preloads?: Array<{ src: string; as?: string; media?: string }>;
}

const CustomHead = ({
  title,
  description,
  image,
  url,
  scripts,
  noIndex,
  structuredDataType,
  author,
  datePublished,
  dateModified,
  preloads,
}: IProps) => {
  const titles = (title: string) => (
    <>
      <title key="title">{title}</title>
      <meta key="og:title" property="og:title" content={title} />
      <meta key="twitter:title" name="twitter:title" content={title} />
    </>
  );

  const descriptions = (description: string) => (
    <>
      <meta key="description" name="description" content={description} />
      <meta key="og:description" property="og:description" content={description} />
      <meta key="twitter:description" name="twitter:description" content={description} />
    </>
  );

  const urls = (url: string) => (
    <>
      <meta key="og:url" property="og:url" content={url} />
      <meta key="twitter:domain" property="twitter:domain" content={url} />
      <meta key="twitter:url" property="twitter:url" content={url} />
    </>
  );

  const images = (image: string) => (
    <>
      <meta key="og:image" property="og:image" content={image} />
      <meta key="twitter:image" name="twitter:image" content={image} />
    </>
  );

  const noIndexTag = <meta name="robots" content="noindex,nofollow" />;

  const renderScripts = (scripts: string[]) =>
    scripts.map((script: string, i: number) => <Script key={`script-item-${i}`} src={script} />);

  return (
    <Head>
      {title && titles(title)}
      {description && descriptions(description)}
      {url && urls(url)}
      {image && images(image)}
      {scripts && renderScripts(scripts)}
      {noIndex || (isStaging && noIndexTag)}
      {structuredDataType && (
        <StructuredDataNewsArticle
          headline={title}
          image={image}
          author={author}
          datePublished={datePublished}
          dateModified={dateModified}
        />
      )}
      {preloads?.map((preload, index) => (
        <link
          rel="preload"
          as={preload.as}
          href={preload.src}
          key={`pre-${index}`}
          fetchPriority="high"
          media={preload.media ?? 'all'}
        />
      ))}
    </Head>
  );
};

export default CustomHead;
